/**
 * Texto mostrado quando a recusa não traz motivo legível — corpo vazio, HTTP sem
 * envelope, falha de transporte (rede, timeout, HTML do Nginx no lugar do JSON) ou
 * erro interno do backend.
 */
export const MENSAGEM_RECUSA_PADRAO = 'Não foi possível processar o arquivo. Tente novamente ou fale com o suporte.';

/**
 * Texto do "Enviar Estudo" quando a falha é técnica. A `save-v2` grava o estudo
 * (já pronto para a fila) ANTES de criar cenário e bloco: um erro interno depois
 * disso, ou a conexão caindo antes da resposta, pode deixar o estudo recebido.
 * Reenviar sem conferir duplicaria o estudo na fila do worker.
 */
export const MENSAGEM_ENVIO_NAO_CONFIRMADO = 'Não conseguimos confirmar se o estudo foi recebido. '
  + 'Confira a lista de estudos antes de enviar de novo. Se ele não aparecer, escolha o arquivo '
  + 'e tente novamente ou fale com o suporte.';

const statusDe = (error: unknown): unknown => (
  typeof error === 'object' && error !== null ? (error as { status?: unknown }).status : undefined
);

/**
 * Recusa do pedido pela API: `code 400` no envelope. É o que respondem o
 * validador do ZIP (`verificar_zip_validation_exception`,
 * `verificar_zip_json_exception`) e a validação dos fatores de ponderação.
 */
export function ehRecusaDaValidacao(error: unknown): boolean {
  return statusDe(error) === 400;
}

/**
 * Falha técnica, sem motivo que sirva ao cliente:
 * - erro de transporte do RTK Query (`status` string: `FETCH_ERROR`,
 *   `PARSING_ERROR`, `TIMEOUT_ERROR`...), em que `error.data` é o texto do fetch
 *   ("TypeError: Failed to fetch");
 * - erro interno (`code >= 500`): o except genérico do backend responde
 *   `base.messages.http_errors.500` com `str(e)` do Python na message, o que
 *   vazaria caminho do NFS ou detalhe do banco para o cliente.
 *
 * A validação indisponível (`code 503`, ver `ehValidacaoIndisponivel`) também é
 * falha técnica, mas tem texto e tratamento próprios: confira ela ANTES desta.
 */
export function ehFalhaTecnica(error: unknown): boolean {
  const status = statusDe(error);
  return typeof status === 'string' || (typeof status === 'number' && status >= 500);
}

/**
 * Remove o par de colchetes que envolve o texto INTEIRO, se houver.
 *
 * O backend monta a recusa com o template i18n `"message": "[description]"`
 * (`previvaz_arquivo.verificar_zip_validation_exception`), então o motivo chega
 * como `[<texto do validador>]`. Só a camada externa sai, e só quando o `[`
 * inicial fecha no `]` final: `[a] e [b]` fica como está, `[[a] e [b]]` vira
 * `[a] e [b]`. Colchetes internos e quebras de linha são preservados.
 */
export function removerColchetesExternos(texto: string): string {
  const aparado = texto.trim();
  if (!aparado.startsWith('[') || !aparado.endsWith(']')) {
    return aparado;
  }

  let profundidade = 0;
  for (let i = 0; i < aparado.length; i += 1) {
    if (aparado[i] === '[') profundidade += 1;
    if (aparado[i] === ']') profundidade -= 1;
    // O colchete de abertura fechou antes do fim: ele não envolve o texto todo.
    if (profundidade === 0 && i < aparado.length - 1) {
      return aparado;
    }
  }

  return profundidade === 0 ? aparado.slice(1, -1).trim() : aparado;
}

const textoDe = (valor: unknown): string => (
  typeof valor === 'string' ? removerColchetesExternos(valor) : ''
);

const messageDe = (error: unknown): string => (
  typeof error === 'object' && error !== null ? textoDe((error as { data?: unknown }).data) : ''
);

/**
 * Validador do ZIP indisponível: `code 503` no envelope, chave
 * `previvaz_arquivo.validacao_indisponivel` da `verify-zip-file` e da `save-v2`
 * (V1.4). A falha é da infraestrutura do servidor (NFS de assets, disco,
 * permissão, memória), não do arquivo: a `save-v2` não grava nem enfileira o
 * estudo, e o time interno recebe o alerta. A `message` já vem escrita para o
 * cliente ("tente mais tarde ou fale com o suporte").
 *
 * Exige texto na `message`. Um 503 de HTTP (proxy), que o `normalizeResult`
 * entrega com `data` undefined, não passou pelo backend — nem alertou ninguém — e
 * segue como falha técnica comum.
 */
export function ehValidacaoIndisponivel(error: unknown): boolean {
  return statusDe(error) === 503 && messageDe(error) !== '';
}

/**
 * Extrai o motivo de uma recusa da API para mostrar ao cliente.
 *
 * Lê o `ResponseError` montado por `normalizeResult` (services/api/base.tsx):
 * a `message` do envelope fica em `error.data` e a `description` em
 * `error.description`. Prioridade: message → description → fallback.
 *
 * Validação indisponível (`ehValidacaoIndisponivel`) devolve a `message`, que o
 * backend escreveu para o cliente. As demais falhas técnicas (transporte ou
 * `code >= 500`, ver `ehFalhaTecnica`) caem direto no fallback: o texto ali é
 * técnico e não diz nada ao cliente.
 */
export function extrairMensagemRecusa(error: unknown): string {
  if (ehValidacaoIndisponivel(error)) {
    return messageDe(error);
  }

  if (typeof error !== 'object' || error === null || ehFalhaTecnica(error)) {
    return MENSAGEM_RECUSA_PADRAO;
  }

  const { data, description } = error as { data?: unknown, description?: unknown };
  return textoDe(data) || textoDe(description) || MENSAGEM_RECUSA_PADRAO;
}
